// result-verifier-evidence.ts
//
// Evidence-loop + disabled-assertion scan, split out of result-verifier.ts
// to keep both files under the 200-line budget. Runs verification recipes
// declared in the worker's result.evidence[] (§5.4 steps 7-9): report path
// scope, recipe approval + ticket's own recipe (SOL-04), exit-code
// interpretation of the controller's own run (testcase must be RED, failing
// for missing behavior, not import/syntax errors).
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { AutopilotResult } from "./autopilot-result-schema.js";
import {
  checkRecipeIsApproved,
  checkReportFileExists,
  checkReportPathInStaging,
  containsDisabledAssertion,
  hasImportErrorMarker,
  isImportOrSyntaxErrorOnly,
} from "./result-verifier-checks.js";
import type {
  RunVerificationRecipeResult,
  VerificationRecipe,
} from "./verification-recipe-runner.js";

export interface EvaluateEvidenceInput {
  evidence: AutopilotResult["evidence"];
  stagingDir: string;
  approvedRecipes: VerificationRecipe[];
  runRecipe: (
    recipe: VerificationRecipe,
    evidenceIndex: number,
  ) => Promise<RunVerificationRecipeResult>;
  isTestcaseAttempt?: boolean;
  /** SOL-04: the ticket's recipe (see VerifyAttemptResultInput.expectedRecipeId). */
  expectedRecipeId?: string;
}

type Evidence = AutopilotResult["evidence"][number];

/** SOL-04: evidence naming a recipe other than the ticket's cannot discharge it. */
function ticketRecipeReason(evidence: Evidence, expected: string | undefined): string | undefined {
  if (expected === undefined || evidence.recipe_id === expected) return undefined;
  return `evidence_recipe_not_ticket_recipe: "${evidence.recipe_id}" — ticket yêu cầu recipe "${expected}"`;
}

/**
 * CXR-R3 (Codex rereview 2026-10-02): a test behind `npm test`/a shell dies by
 * a signal while the wrapper exits NORMALLY with 128+signal — no `null` exit
 * code for the CXR-06 guard to catch. Only these signal deaths are refused as
 * a RED testcase (any other nonzero still may be a plain assertion failure);
 * not every code ≥128 is read as a signal.
 */
const WRAPPER_SIGNAL_EXIT_CODES: Readonly<Record<number, string>> = {
  130: "SIGINT",
  134: "SIGABRT",
  137: "SIGKILL",
  139: "SIGSEGV",
  143: "SIGTERM",
};

/**
 * Phase 06d R4 (F-15, spec E18): a test for a NEW symbol/module cannot stub it
 * (allowed_paths = the test file), so a top-level import is always red for
 * ImportError. The refusal stays; the fixed hint says how to be red for the
 * right reason. Only for the 4 import patterns — SyntaxError alone keeps the
 * plain reason (N4).
 */
const RED_IMPORT_HINT =
  ' — gợi ý RED đúng lý do: symbol mới trong module có sẵn ⇒ getattr(module, "name", None) rồi assert; ' +
  'module mới ⇒ assert importlib.util.find_spec("pkg.mod") is not None (Python, parent phải có sẵn) / ' +
  "JS-TS: specifier là biến, không literal (literal ⇒ tsc báo thiếu module, bị từ chối lại): " +
  'const specifier = "../src/new-mod.js"; try { mod = await import(specifier) } catch { mod = undefined }; ' +
  "expect(mod).toBeDefined(); " +
  "không in message lỗi gốc, không import tên ở đầu file";

/**
 * Exit-code interpretation of the controller's OWN run (exitCode non-null here — a null one is rejected before):
 * non-testcase must pass; testcase must be RED (pack command-adapters.md:
 * "outcome=done chỉ khi test đỏ đúng lý do") for missing behavior, not an
 * import/syntax error nor a wrapper's signal exit (CXR-R3). The worker's declared `exit_code`/`sha256` are NOT the
 * verdict: this run is (SOL-04) — a self-report can neither pass a failing
 * recipe nor fail a passing one, and sha256 is a "0"×64 placeholder by the
 * prompt contract (no hashing tool is granted to workers).
 */
function exitCodeReason(
  evidence: Evidence,
  runResult: RunVerificationRecipeResult,
  isTestcaseAttempt: boolean,
): string | undefined {
  const combinedOutput = `${runResult.stdoutTail}\n${runResult.stderrTail}`;
  if (runResult.exitCode === 0) {
    return isTestcaseAttempt
      ? `red_testcase_passed: ${evidence.recipe_id} => 0 — testcase phải RED (đỏ vì thiếu behavior) trước khi implement`
      : undefined;
  }
  if (!isTestcaseAttempt) {
    return `verification_recipe_exit_nonzero: ${evidence.recipe_id} => ${runResult.exitCode}`;
  }
  const signal = WRAPPER_SIGNAL_EXIT_CODES[runResult.exitCode ?? -1];
  if (signal !== undefined) {
    return (
      `red_testcase_exit_looks_like_signal: ${evidence.recipe_id} => ${runResult.exitCode} ` +
      `(128+${(runResult.exitCode ?? 0) - 128} = ${signal}) — tiến trình test bị kill, ` +
      "không có assertion nào chứng minh behavior còn thiếu"
    );
  }
  if (!isImportOrSyntaxErrorOnly(combinedOutput)) return undefined;
  const reason = `red_testcase_failing_for_import_or_syntax_error: ${combinedOutput.trim().slice(0, 200)}`;
  return hasImportErrorMarker(combinedOutput) ? `${reason}${RED_IMPORT_HINT}` : reason;
}

/** Runs every evidence entry's recipe; returns accumulated rejection reasons (empty = all pass). */
export async function evaluateEvidence(input: EvaluateEvidenceInput): Promise<string[]> {
  const reasons: string[] = [];
  const expected = input.expectedRecipeId;
  if (expected !== undefined && !input.evidence.some((e) => e?.recipe_id === expected)) {
    reasons.push(
      `required_recipe_missing: ticket yêu cầu evidence cho recipe "${expected}" — recipe khác trong profile không thay được`,
    );
  }
  for (let i = 0; i < input.evidence.length; i += 1) {
    const evidence = input.evidence[i];
    if (!evidence) continue;

    const stagingReason = checkReportPathInStaging(evidence.report_path, input.stagingDir);
    if (stagingReason) reasons.push(stagingReason);

    const recipeReason = checkRecipeIsApproved(evidence.recipe_id, input.approvedRecipes);
    if (recipeReason) reasons.push(recipeReason);
    const notTicketRecipe = ticketRecipeReason(evidence, expected);
    if (notTicketRecipe) reasons.push(notTicketRecipe);
    if (stagingReason || recipeReason || notTicketRecipe) continue;

    const recipe = input.approvedRecipes.find((r) => r.recipe_id === evidence.recipe_id);
    if (!recipe) continue;
    // R-06: runRecipe can reject (async spawn failure) or throw synchronously
    // (e.g. runVerificationRecipe when recipe.argv is empty) -- either way
    // this must not escape uncaught and crash the supervisor loop.
    let runResult: RunVerificationRecipeResult;
    try {
      runResult = await input.runRecipe(recipe, i);
    } catch {
      reasons.push(`verification_recipe_spawn_failed:${evidence.recipe_id}`);
      continue;
    }

    // R-11: a timed-out attempt must be a real failure reason for EVERY
    // attempt kind (including testcase), checked before the exit-code
    // branch below -- exitCode is null on timeout, which used to either
    // pass silently (testcase) or misreport as exit_nonzero (non-testcase).
    if (runResult.timedOut) {
      reasons.push(`verification_recipe_timed_out:${evidence.recipe_id}`);
      continue;
    }
    // CXR-06: killed by a signal (OOM, outside kill, its own SIGKILL) ⇒ no
    // exit code, so no assertion proved anything — never a valid RED.
    if (runResult.exitCode === null) {
      const by = runResult.signal === undefined ? "" : ` (${runResult.signal})`;
      reasons.push(`verification_recipe_terminated_without_exit_code:${evidence.recipe_id}${by}`);
      continue;
    }

    if (!runResult.reportExists) {
      const missingReport = await checkReportFileExists(runResult.reportPath);
      if (missingReport) reasons.push(missingReport);
    }

    const exitReason = exitCodeReason(evidence, runResult, input.isTestcaseAttempt === true);
    if (exitReason) reasons.push(exitReason);
  }
  return reasons;
}

/** Scans touched (added/modified) files for a disabled-assertion marker (it.skip/xit/@pytest.mark.skip/.only). */
export async function scanForDisabledAssertions(
  repoDir: string,
  touchedPaths: string[],
): Promise<string | undefined> {
  for (const relPath of touchedPaths) {
    let content: string;
    try {
      content = await fs.readFile(join(repoDir, relPath), "utf8");
    } catch {
      continue; // deleted file, nothing to scan
    }
    if (containsDisabledAssertion(content)) {
      return `disabled_assertion_detected: ${relPath}`;
    }
  }
  return undefined;
}
